import * as React from 'react';

import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
      },
      shape: {
        default: 'h-5 text-xs',
        rounded: 'rounded-full p-0 h-5 w-5 flex flex-col items-center justify-center text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  count?: number;
  showZero?: boolean;
  size?: 'default' | 'small';
  overflowCount?: number;
  color?: string;
  offset?: [number, number];
}

function Badge({
  className,
  variant,
  shape,
  count,
  showZero,
  size = 'default',
  overflowCount,
  color,
  offset = [0, 0],
  children,
  ...props
}: BadgeProps) {
  const hasCount = count !== undefined && (showZero || count > 0);
  const displayCount =
    overflowCount !== undefined && count !== undefined && count > overflowCount ? `${overflowCount}+` : count;

  if (hasCount) {
    return (
      <div className="relative inline-flex" style={{ display: 'inline-flex' }}>
        {children}
        <span
          className={cn(
            'absolute right-0 top-0 flex items-center justify-center rounded-full font-semibold',
            size === 'small' ? 'h-4 min-w-[16px] px-1 text-[10px]' : 'h-5 min-w-[20px] px-1 text-xs',
            variant === 'destructive' || variant === 'default'
              ? 'bg-destructive text-destructive-foreground'
              : 'bg-destructive text-destructive-foreground',
            className,
          )}
          style={{
            transform: `translate(calc(50% - ${offset[0]}px), calc(-50% - ${offset[1]}px))`,
            ...(color && { background: color, color: '#000' }),
          }}
          {...props}
        >
          {displayCount}
        </span>
      </div>
    );
  }

  return <>{children}</>;
}

export { Badge, badgeVariants };
